import {
	ApiOutlined,
	CalendarOutlined,
	CameraOutlined,
	DashboardOutlined,
	HomeOutlined,
	LogoutOutlined,
	MenuFoldOutlined,
	MenuUnfoldOutlined,
	PictureOutlined,
	TeamOutlined,
	UserOutlined,
	VideoCameraOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Dropdown, Layout, Menu, Typography } from "antd";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useLogout } from "@/modules/auth/hooks/useAuth";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { UserRole } from "@shared/types";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export function MainLayout() {
	const [collapsed, setCollapsed] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const user = useAuthStore((state) => state.user);
	const { mutate: logout, isPending: isLoggingOut } = useLogout();

	const isAdmin = user?.role?.id === UserRole.ADMIN;

	const menuItems = [
		{ key: "/dashboard", icon: <DashboardOutlined />, label: "Dashboard" },
		{ key: "/wedding-halls", icon: <HomeOutlined />, label: "To'yxonalar" },
		{
			key: "devices",
			icon: <VideoCameraOutlined />,
			label: "Qurilmalar",
			children: [
				{ key: "/nvrs", icon: <VideoCameraOutlined />, label: "NVR" },
				{ key: "/cameras", icon: <CameraOutlined />, label: "Kameralar" },
			],
		},
		{ key: "/artists", icon: <TeamOutlined />, label: "Sanatkorlar" },
		{ key: "/events", icon: <CalendarOutlined />, label: "Tadbirlar" },
		{ key: "/snapshots", icon: <PictureOutlined />, label: "Snapshotlar" },
		...(isAdmin
			? [
					{ type: "divider" as const },
					{ key: "/users", icon: <UserOutlined />, label: "Foydalanuvchilar" },
					{ key: "/integrations", icon: <ApiOutlined />, label: "Integration" },
				]
			: []),
	];

	const userMenuItems = [
		{ key: "profile", icon: <UserOutlined />, label: "Profil" },
		{ type: "divider" as const },
		{ key: "logout", icon: <LogoutOutlined />, label: "Chiqish", danger: true },
	];

	const handleUserMenuClick = ({ key }: { key: string }) => {
		if (key === "logout") logout();
	};

	const selectedKey = "/" + location.pathname.split("/")[1];

	return (
		<Layout className="min-h-screen">
			<Sider trigger={null} collapsible collapsed={collapsed} theme="dark" width={230}>
				<div className="h-16 flex items-center justify-center">
					<Text strong className="text-white text-lg">
						{collapsed ? "WHS" : "WeddingHall Stats"}
					</Text>
				</div>
				<Menu
					theme="dark"
					mode="inline"
					selectedKeys={[selectedKey]}
					defaultOpenKeys={["/nvrs", "/cameras"].includes(selectedKey) ? ["devices"] : []}
					items={menuItems}
					onClick={({ key }) => navigate(key)}
				/>
			</Sider>
			<Layout>
				<Header className="bg-white px-4 flex items-center justify-between shadow-sm">
					<Button
						type="text"
						icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
						onClick={() => setCollapsed(!collapsed)}
						className="text-lg"
					/>
					<Dropdown
						menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
						placement="bottomRight"
						trigger={["click"]}
						disabled={isLoggingOut}
					>
						<div className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 px-3 py-1 rounded">
							<Avatar icon={<UserOutlined />} />
							<Text className="hidden sm:inline">{user?.name}</Text>
						</div>
					</Dropdown>
				</Header>
				<Content className="m-4 p-6 bg-white rounded-lg min-h-70">
					<Outlet />
				</Content>
			</Layout>
		</Layout>
	);
}
